import { useCallback, useEffect, useRef, useState } from "react";
import {
  getLoopingDotIndex,
  getSliderDotCount,
  resolveLoopingDotTarget,
} from "@/utils/sliderDots";

export function getMobileScrollStep(scrollEl) {
  if (!scrollEl?.firstElementChild) return 0;

  const style = getComputedStyle(scrollEl);
  const gap =
    parseFloat(style.columnGap || style.gap || "0") ||
    parseFloat(style.rowGap || "0") ||
    0;

  return scrollEl.firstElementChild.offsetWidth + gap;
}

function canScrollHorizontally(el) {
  if (!el) return false;
  return el.scrollWidth > el.clientWidth + 2;
}

export default function useMobileScrollDots(itemCount, resetKey) {
  const nodeRef = useRef(null);
  const [scrollEl, setScrollEl] = useState(null);
  const [activeDot, setActiveDot] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  const scrollRef = useCallback((node) => {
    nodeRef.current = node;
    setScrollEl(node);
  }, []);

  const updateCanScroll = useCallback(() => {
    setCanScroll(canScrollHorizontally(nodeRef.current));
  }, []);

  useEffect(() => {
    setActiveDot(0);
    if (nodeRef.current) nodeRef.current.scrollLeft = 0;
    updateCanScroll();
  }, [resetKey, itemCount, scrollEl, updateCanScroll]);

  useEffect(() => {
    const el = scrollEl;
    if (!el) {
      setCanScroll(false);
      return undefined;
    }

    updateCanScroll();

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateCanScroll)
        : null;
    ro?.observe(el);

    const onResize = () => updateCanScroll();
    window.addEventListener("resize", onResize);

    const images = el.querySelectorAll("img");
    const onImageLoad = () => updateCanScroll();
    images.forEach((img) => {
      if (!img.complete) img.addEventListener("load", onImageLoad);
    });

    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", onResize);
      images.forEach((img) => img.removeEventListener("load", onImageLoad));
    };
  }, [scrollEl, itemCount, resetKey, updateCanScroll]);

  const mobileDots = canScroll ? getSliderDotCount(itemCount) : 0;

  const getSlideIndex = useCallback(
    (el) => {
      const step = getMobileScrollStep(el);
      if (!step) return 0;
      const maxIndex = Math.max(0, (Number(itemCount) || 1) - 1);
      return Math.min(maxIndex, Math.max(0, Math.round(el.scrollLeft / step)));
    },
    [itemCount]
  );

  const onScroll = useCallback(() => {
    const el = nodeRef.current;
    if (!el || mobileDots <= 1) return;

    setActiveDot(getLoopingDotIndex(getSlideIndex(el), mobileDots));
  }, [getSlideIndex, mobileDots]);

  const goToDot = useCallback(
    (index) => {
      const el = nodeRef.current;
      if (!el) return;

      const step = getMobileScrollStep(el);
      if (!step) return;

      const target = resolveLoopingDotTarget(
        index,
        getSlideIndex(el),
        itemCount,
        mobileDots
      );
      el.scrollTo({ left: target * step, behavior: "smooth" });
      setActiveDot(getLoopingDotIndex(target, mobileDots));
    },
    [getSlideIndex, itemCount, mobileDots]
  );

  return { scrollRef, activeDot, mobileDots, onScroll, goToDot, canScroll };
}
